import throttle from "lodash/throttle";
import classNames from "classnames";
import Sb from "../abstract/StatefulBehavior";

/**
 * A versatile behavior for visually updating a nav element
 * to show which section of a page is currently in view.
 * Works with a group of anchors (currently for desktop) or a select element
 * (currently for mobile). Also watches user scrolling (if appropriate)
 * and updates the active anchor or select value based on scroll position.
 */
export default class HighlightActivePageSection extends Sb {
  constructor(el, props, refs) {
    super();

    this.el = el;
    this.props = props;
    this.refs = refs;

    this.state = {
      active: this.sectionIds[0],
    };

    this.scrollPaused = false;
    this.initAnchorClasses = !this.anchors
      ? []
      : this.anchors.map((anchor) => anchor.className);

    this.update();
    this.bindEvents();
    this.polyfillSmoothScroll();
  }

  get sectionIds() {
    return Object.keys(this.props.sections);
  }

  get sectionHashes() {
    return this.sectionIds.map((id) => `#${id}`);
  }

  get anchors() {
    const { anchor } = this.refs;
    if (!anchor) return undefined;
    return Array.isArray(anchor) ? anchor : Array(anchor);
  }

  get shouldWatchSelect() {
    return !!this.refs.select;
  }

  get shouldWatchScroll() {
    return this.sectionIds.length > 1;
  }

  get shouldUpdateAnchors() {
    return !!this.anchors;
  }

  get supportsSmoothScroll() {
    // Due to bugs with using `scroll-behavior: smooth` in conjunction with `window.location.href`,
    // we're avoiding CSS smooth-scrolling for now and just using `scrollIntoView`
    // return CSS.supports("scroll-behavior: smooth");
    return false;
  }

  get prefersReducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  get scrollBehavior() {
    return this.prefersReducedMotion ? "auto" : "smooth";
  }

  update = () => {
    this.updateSelectValue();
    this.updateAnchorClasses();
  };

  updateSelectValue = () => {
    if (!this.shouldWatchSelect) return;

    this.refs.select.value = this.state.active;
  };

  updateAnchorClasses() {
    if (!this.shouldUpdateAnchors) return;

    this.anchors.forEach((anchor, index) => {
      const href = anchor.getAttribute("href");
      const activeHash = `#${this.state.active}`;
      anchor.className = classNames(this.initAnchorClasses[index], {
        "m-sibling-nav__link--is-active": href === activeHash,
      });
    });
  }

  // used to prevent scroll callback from firing
  // while browser smooth-scrolling is in progress
  temporarilyPauseScroll() {
    if (!this.shouldWatchScroll) return;

    this.scrollPaused = true;

    setTimeout(() => {
      this.scrollPaused = false;
    }, 750);
  }

  pushToHistory(targetId) {
    const currentUrl = new URL(window.location);
    currentUrl.hash = targetId;
    window.history.pushState({ id: targetId }, null, currentUrl);
  }

  getScrollMargin(node) {
    const computedStyles = window.getComputedStyle(node);
    const scrollMarginPx =
      computedStyles.getPropertyValue("scroll-margin-top") || 0;
    return parseInt(scrollMarginPx, 10);
  }

  handleAnchorClick = (event) => {
    const target = event.target.getAttribute("href");
    const targetId = target.replace("#", "");

    if (targetId === this.state.active) return;

    this.setState({ active: targetId });
    this.temporarilyPauseScroll();

    // Use polyfilled JS smooth-scrolling for Safari and other browsers
    // that don't support css `scroll-behavior` property
    if (!this.supportsSmoothScroll) {
      event.preventDefault();
      const targetNode = document.querySelector(target);

      if (!targetNode) return;

      const position =
        targetNode.getBoundingClientRect().top +
        document.documentElement.scrollTop -
        this.getScrollMargin(targetNode);

      window.scroll({
        behavior: this.scrollBehavior,
        top: position,
      });
      this.pushToHistory(target);
    }
  };

  handleSelect = (event) => {
    const {
      target: { value },
    } = event;
    this.setState({ active: value });
    this.temporarilyPauseScroll();

    // Use polyfilled JS smooth-scrolling for Safari and other browsers
    // that don't support css `scroll-behavior` property
    if (!this.supportsSmoothScroll) {
      const targetNode = document.querySelector(`#${value}`);
      targetNode.scrollIntoView({ behavior: this.scrollBehavior });

      this.pushToHistory(value);
    } else {
      window.location.href = `#${value}`;
    }
  };

  handleScroll = () => {
    if (this.scrollPaused) return;

    let lastId = null;

    this.sectionIds.forEach((id) => {
      const node = document.querySelector(`#${id}`);

      if (!node) return;

      const { top } = node.getBoundingClientRect();
      const scrollMargin = this.getScrollMargin(node);

      // scrolling via select element seems to set top just more than
      // computed scroll margin, so we add 1px
      if (top <= scrollMargin + 1) lastId = id;
    });

    if (!lastId || this.state.active === lastId) return;
    this.setState({ active: lastId });
  };

  bindEvents() {
    if (this.shouldWatchSelect) {
      this.refs.select.addEventListener("change", this.handleSelect);
    }

    if (this.shouldWatchScroll) {
      window.addEventListener("scroll", throttle(this.handleScroll, 100));
    }

    if (this.shouldUpdateAnchors) {
      this.anchors.forEach((anchor) =>
        anchor.addEventListener("click", this.handleAnchorClick)
      );
    }
  }

  polyfillSmoothScroll() {
    if (!this.supportsSmoothScroll) import("scroll-behavior-polyfill");
  }
}
